/**
 * System Status Dashboard - Admin Only
 * Real-time platform health monitoring with live data from /api/health endpoints
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Database,
  Server,
  Wifi,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Zap,
  HardDrive,
  TrendingUp,
  FileWarning,
  Inbox,
  Gauge
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  healthy: { color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', Icon: CheckCircle2 },
  degraded: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', Icon: AlertTriangle },
  unhealthy: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', Icon: XCircle },
  ok: { color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', Icon: CheckCircle2 },
  error: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', Icon: XCircle },
  disconnected: { color: 'text-[#6B6B6B]', bg: 'bg-[#1F1F1F]', border: 'border-[#2F2F2F]', Icon: XCircle },
};

function formatUptime(seconds) {
  if (!seconds) return '0s';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${seconds % 60}s`;
}

function ServiceCard({ name, icon: Icon, status, details }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.disconnected;
  const StatusIcon = config.Icon;

  return (
    <div className={`p-4 bg-[#0D0D0D] border ${config.border} rounded-xl`} data-testid={`service-${name.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#6B6B6B]" />
          <span className="text-sm font-medium text-white">{name}</span>
        </div>
        <Badge className={`${config.bg} ${config.color} border-0`}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {status}
        </Badge>
      </div>
      {details && (
        <div className="space-y-1">
          {Object.entries(details).map(([key, val]) => (
            <div key={key} className="flex justify-between text-xs">
              <span className="text-[#6B6B6B]">{key}</span>
              <span className="text-[#A0A0A0]">{String(val)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color = 'text-white', subtext }) {
  return (
    <div className="p-4 bg-[#0D0D0D] border border-[#1F1F1F] rounded-xl" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-[#6B6B6B]">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {subtext && <p className="text-xs text-[#6B6B6B] mt-1">{subtext}</p>}
    </div>
  );
}

export default function SystemStatusPage() {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [dlq, setDlq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const getToken = () => localStorage.getItem('token');

  const fetchAll = useCallback(async () => {
    const headers = { 'Authorization': `Bearer ${getToken()}` };
    try {
      const [healthRes, metricsRes, dlqRes] = await Promise.all([
        fetch(`${API_URL}/api/health`, { headers }),
        fetch(`${API_URL}/api/health/metrics`, { headers }),
        fetch(`${API_URL}/api/health/dlq`, { headers }),
      ]);
      const [healthData, metricsData, dlqData] = await Promise.all([
        healthRes.json(),
        metricsRes.json(),
        dlqRes.json(),
      ]);
      setHealth(healthData);
      setMetrics(metricsData);
      setDlq(dlqData);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAll]);

  const overallStatus = health?.status || 'unknown';
  const overallConfig = STATUS_CONFIG[overallStatus] || STATUS_CONFIG.disconnected;

  return (
    <div className="space-y-6" data-testid="system-status-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">System Status</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Real-time platform health monitoring
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-[#6B6B6B]">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className={`border-[#2F2F2F] ${autoRefresh ? 'text-emerald-400' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-toggle"
          >
            <Activity className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-[#2F2F2F]"
            onClick={fetchAll}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className={`p-4 ${overallConfig.bg} border ${overallConfig.border} rounded-xl flex items-center justify-between`} data-testid="overall-status-banner">
        <div className="flex items-center gap-3">
          <overallConfig.Icon className={`w-6 h-6 ${overallConfig.color}`} />
          <div>
            <p className={`font-semibold ${overallConfig.color}`}>
              Platform is {overallStatus}
            </p>
            <p className="text-xs text-[#A0A0A0]">
              Uptime: {formatUptime(health?.checks?.uptime || metrics?.uptime_seconds)}
            </p>
          </div>
        </div>
        <span className="text-xs text-[#6B6B6B]">{health?.timestamp}</span>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Jobs"
          value={metrics?.jobs?.total || 0}
          icon={Zap}
          color="text-white"
        />
        <MetricCard
          label="Completed"
          value={metrics?.jobs?.completed || 0}
          icon={CheckCircle2}
          color="text-emerald-400"
          subtext={metrics?.jobs?.total ? `${Math.round((metrics.jobs.completed / metrics.jobs.total) * 100)}% success rate` : ''}
        />
        <MetricCard
          label="Active"
          value={(metrics?.jobs?.queued || 0) + (metrics?.jobs?.processing || 0)}
          icon={TrendingUp}
          color="text-blue-400"
          subtext={`${metrics?.jobs?.queued || 0} queued, ${metrics?.jobs?.processing || 0} processing`}
        />
        <MetricCard
          label="DLQ Events"
          value={metrics?.dlq_count || 0}
          icon={FileWarning}
          color={metrics?.dlq_count > 0 ? 'text-red-400' : 'text-emerald-400'}
          subtext={metrics?.dlq_count > 0 ? 'Requires attention' : 'No failed events'}
        />
      </div>

      {/* Services Grid */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-3">Service Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ServiceCard
            name="MongoDB"
            icon={Database}
            status={health?.checks?.mongodb?.status || 'unknown'}
            details={{
              'Latency': `${health?.checks?.mongodb?.latency_ms || 0}ms`,
              'Evidence Docs': metrics?.evidence?.total || 0,
            }}
          />
          <ServiceCard
            name="Redis"
            icon={Server}
            status={health?.checks?.redis?.status === 'healthy' ? 'healthy' : health?.checks?.redis?.status || 'unknown'}
            details={{
              'Connected': health?.checks?.redis?.connected ? 'Yes' : 'No',
              'Event Bus': health?.checks?.redis?.connected ? 'Active' : 'Inactive',
            }}
          />
          <ServiceCard
            name="Weaviate"
            icon={HardDrive}
            status={health?.checks?.weaviate?.status || 'unknown'}
            details={{
              'Connected': health?.checks?.weaviate?.connected ? 'Yes' : 'No',
              'Documents': health?.checks?.weaviate?.documents || 0,
            }}
          />
          <ServiceCard
            name="Event Streams"
            icon={Wifi}
            status={health?.checks?.event_streams?.status || 'unknown'}
            details={health?.checks?.event_streams?.streams ? 
              Object.fromEntries(
                Object.entries(health.checks.event_streams.streams).map(([k, v]) => [k.split(':').pop(), `${v.length} msgs`])
              ) : {}
            }
          />
        </div>
      </div>

      {/* Job Processing & DLQ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Job Processing */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#1F1F1F] flex items-center gap-2">
            <Gauge className="w-4 h-4 text-blue-400" />
            <h3 className="font-medium text-white">Job Processing</h3>
          </div>
          <div className="p-4 space-y-3">
            {metrics?.jobs && (
              <>
                <JobBar label="Completed" count={metrics.jobs.completed} total={metrics.jobs.total} color="bg-emerald-500" />
                <JobBar label="Processing" count={metrics.jobs.processing} total={metrics.jobs.total} color="bg-blue-500" />
                <JobBar label="Queued" count={metrics.jobs.queued} total={metrics.jobs.total} color="bg-amber-500" />
                <JobBar label="Failed" count={metrics.jobs.failed} total={metrics.jobs.total} color="bg-red-500" />
              </>
            )}
          </div>
        </div>

        {/* Dead Letter Queue */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-red-400" />
              <h3 className="font-medium text-white">Dead Letter Queue</h3>
            </div>
            <Badge className={dlq?.count > 0 ? 'bg-red-500/20 text-red-400 border-0' : 'bg-emerald-500/20 text-emerald-400 border-0'}>
              {dlq?.count || 0} events
            </Badge>
          </div>
          <div className="p-4">
            {dlq?.events?.length > 0 ? (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {dlq.events.map((evt, i) => (
                  <div key={i} className="p-2 bg-[#0D0D0D] rounded-lg border border-red-500/20 text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-red-400">{evt.event_type}</span>
                      <span className="text-[#6B6B6B]">{evt.timestamp}</span>
                    </div>
                    {evt.error && <p className="text-[#A0A0A0]">{evt.error}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 text-[#6B6B6B]">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                <p className="text-sm">No failed events</p>
                <p className="text-xs">All events processed successfully</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function JobBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-[#A0A0A0]">{label}</span>
        <span className="text-white">{count} ({pct}%)</span>
      </div>
      <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
